import type { 
  Curve,
  ParsedData, 
  SurveyMeasurement
} from '../../../../interfaces/toolData';
import type { FileConfig } from '../types/FileConfig';
import { cleanTimeSeriesData } from '../../../dataQuality';
import { ColumnNameMapper } from '../../utils/ColumnNameMapper';
import { getUnitForColumn } from './ColumnMapping';

// Common CSV structure after initial parsing
interface ParsedCSVStructure {
  headers: string[];
  rawData: Record<string, string>[];
  metadata: Record<string, string>;
  timeColumnName: string | null;
  parseErrors: string[];
}

// Helper function to find column names with variations
function findColumnName(headers: string[], possibleNames: string[]): string | null {
  for (const name of possibleNames) {
    const found = headers.find(header => header.toLowerCase().includes(name.toLowerCase()));
    if (found) return found;
  }
  return null;
}

export class SurveyProcessor {
  static process(
    parsedStructure: ParsedCSVStructure, 
    config: FileConfig, 
    _filename?: string
  ): ParsedData {
    const { rawData, metadata, timeColumnName, parseErrors } = parsedStructure;
    
    // Get actual column names from the data (flexible mapping)
    const dataHeaders = Object.keys(rawData[0] || {});
    const inclinationCol = findColumnName(dataHeaders, ['Inclination', 'Inclination (degrees)', 'INC (degrees)', 'INC']);
    const azimuthCol = findColumnName(dataHeaders, ['Azimuth', 'Azimuth (degrees)', 'AZM (degrees)', 'AZM']); 
    const toolFaceCol = findColumnName(dataHeaders, ['Tool-Face', 'Gravity Toolface', 'Gravity Tool-Face', 'GTFA (degrees)', 'GTFA']);
    
    if (!inclinationCol || !azimuthCol) {
      throw new Error(`Missing required survey columns. Found headers: ${dataHeaders.join(', ')}`);
    }
    
    // Get optional column names once outside the map
    const dipAngleCol = findColumnName(dataHeaders, ['Dip Angle', 'Dip Angle (degrees)', 'DIPA (degrees)', 'DIPA']);
    const temperatureCol = findColumnName(dataHeaders, ['Temperature', 'Temperature (celcius)', 'Survey Temperature (celcius)']);
    const magFCol = findColumnName(dataHeaders, ['MagF', 'Magnetic Field', 'Magnetic Field (gauss)', 'MAGF (gauss)', 'MAGF']);
    const gravityCol = findColumnName(dataHeaders, ['Total Grav', 'Gravity (g)', 'GRAV (g)', 'GRAV', 'Gravity']);
    
    // New 3.4.6 columns - accelerometer components
    const axCol = findColumnName(dataHeaders, ['Ax (g)', 'Ax', 'AX']);
    const ayCol = findColumnName(dataHeaders, ['Ay (g)', 'Ay', 'AY']);
    const azCol = findColumnName(dataHeaders, ['Az (g)', 'Az', 'AZ']);
    const ax2Col = findColumnName(dataHeaders, ['Ax2 (g)', 'Ax2', 'AX2']);
    const ay2Col = findColumnName(dataHeaders, ['Ay2 (g)', 'Ay2', 'AY2']);
    const az2Col = findColumnName(dataHeaders, ['Az2 (g)', 'Az2', 'AZ2']);
    
    // New 3.4.6 columns - magnetometer components  
    const mxCol = findColumnName(dataHeaders, ['Mx (gauss)', 'Mx', 'MX']);
    const myCol = findColumnName(dataHeaders, ['My (gauss)', 'My', 'MY']);
    const mzCol = findColumnName(dataHeaders, ['Mz (gauss)', 'Mz', 'MZ']);
    
    // New 3.4.6 columns - quality and status
    const gErrorFactorCol = findColumnName(dataHeaders, ['G Error Factor']);
    const continuousSurveyStatusCol = findColumnName(dataHeaders, ['Continuous Survey Status']);
    
    // Additional toolface column variations for 3.4.6
    const magneticToolFaceCol = findColumnName(dataHeaders, ['Magnetic Toolface', 'Magnetic Toolface (degrees)', 'Magnetic Tool-Face', 'MTFA (degrees)', 'MTFA']);
    
    // Convert to ParsedToolData format with surveys - filter out invalid timestamps
    const allSurveys = rawData.map((row, index) => {
      // Handle timestamp generation or extraction
      let timestamp: Date;
      if (timeColumnName && row[timeColumnName]) {
        timestamp = new Date(row[timeColumnName]);
      } else if (config.generateTimestamps) {
        // Generate timestamp from start time + sampling rate
        const startTime = new Date(metadata[config.startTimeMetadataKey!] || new Date());
        const samplingRate = parseFloat(metadata[config.samplingRateMetadataKey!]?.replace(/[^\d.]/g, '') || '1');
        const intervalMs = 1000 / samplingRate; // Convert Hz to milliseconds
        timestamp = new Date(startTime.getTime() + (index * intervalMs));
      } else {
        // For empty timestamps, return invalid Date rather than current time
        // This allows timestamp inheritance logic to work properly
        timestamp = new Date(NaN);
      }
      
      const survey: SurveyMeasurement = {
        time: timestamp,
        inclination: parseFloat(row[inclinationCol]) || 0,
        azimuth: parseFloat(row[azimuthCol]) || 0,
      };
      
      // Add optional fields if they exist (with flexible column detection)
      if (toolFaceCol && row[toolFaceCol]) survey.toolFace = parseFloat(row[toolFaceCol]);
      if (magneticToolFaceCol && row[magneticToolFaceCol]) survey.magneticToolFace = parseFloat(row[magneticToolFaceCol]);
      if (dipAngleCol && row[dipAngleCol]) survey.dipAngle = parseFloat(row[dipAngleCol]);
      if (temperatureCol && row[temperatureCol]) survey.temperature = parseFloat(row[temperatureCol]);
      if (magFCol && row[magFCol]) survey.magF = parseFloat(row[magFCol]);
      if (gravityCol && row[gravityCol]) survey.totalGrav = parseFloat(row[gravityCol]);
      
      // Accelerometer components - map all variations to standardized gx, gy, gz fields
      if (row['GX']) survey.gx = parseFloat(row['GX']);
      else if (axCol && row[axCol]) survey.gx = parseFloat(row[axCol]);
      
      if (row['GY']) survey.gy = parseFloat(row['GY']);
      else if (ayCol && row[ayCol]) survey.gy = parseFloat(row[ayCol]);
      
      if (row['GZ']) survey.gz = parseFloat(row['GZ']);
      else if (azCol && row[azCol]) survey.gz = parseFloat(row[azCol]);
      
      // New 3.4.6 accelerometer components (direct mapping)
      if (axCol && row[axCol]) survey.ax = parseFloat(row[axCol]);
      if (ayCol && row[ayCol]) survey.ay = parseFloat(row[ayCol]);
      if (azCol && row[azCol]) survey.az = parseFloat(row[azCol]);
      if (ax2Col && row[ax2Col]) survey.ax2 = parseFloat(row[ax2Col]);
      if (ay2Col && row[ay2Col]) survey.ay2 = parseFloat(row[ay2Col]);
      if (az2Col && row[az2Col]) survey.az2 = parseFloat(row[az2Col]);
      
      // Magnetometer components (new 3.4.6)
      if (mxCol && row[mxCol]) survey.mx = parseFloat(row[mxCol]);
      if (myCol && row[myCol]) survey.my = parseFloat(row[myCol]);
      if (mzCol && row[mzCol]) survey.mz = parseFloat(row[mzCol]);
      
      // Quality and status (new 3.4.6)
      if (gErrorFactorCol && row[gErrorFactorCol]) survey.gErrorFactor = parseFloat(row[gErrorFactorCol]);
      if (continuousSurveyStatusCol && row[continuousSurveyStatusCol]) survey.continuousSurveyStatus = parseFloat(row[continuousSurveyStatusCol]);
      
      // Battery status
      if (row['Battery']) survey.battery = parseFloat(row['Battery']);
      if (row['Battery 2']) survey.battery2 = row['Battery 2'];
      
      return survey;
    });
    
    // Use all surveys - timeSeriesCleanup will handle timestamp validation during post-processing
    const surveys = allSurveys;
    
    // Generate curves from survey data for trending/visualization
    // Only include appropriate measurement columns, not metadata/status columns
    const allowedSurveyColumns = [
      // Core survey measurements
      'Inclination', 'Inclination (degrees)', 'INC (degrees)', 'INC',
      'Azimuth', 'Azimuth (degrees)', 'AZM (degrees)', 'AZM',
      'Tool-Face', 'Gravity Toolface', 'Gravity Tool-Face', 'Gravity Toolface (degrees)', 'Gravity Tool-Face (degrees)', 'GTFA (degrees)', 'GTFA',
      'Magnetic Toolface', 'Magnetic Tool-Face', 'Magnetic Toolface (degrees)', 'Magnetic Tool-Face (degrees)', 'MTFA (degrees)', 'MTFA',
      'Dip Angle', 'Dip Angle (degrees)', 'DIPA (degrees)', 'DIPA',
      'Temperature', 'Temperature (Celsius)', 'Temperature (celcius)', 'Survey Temperature (celcius)',
      
      // Magnetic field measurements
      'MagF', 'Magnetic Field', 'Magnetic Field (gauss)', 'MAGF (gauss)', 'MAGF',
      
      // Gravity measurements
      'Total Grav', 'Gravity', 'Gravity (g)', 'GRAV (g)', 'GRAV',
      
      // Accelerometer components (all variations)
      'GX', 'GY', 'GZ', 'Ax', 'Ay', 'Az', 'Ax (g)', 'Ay (g)', 'Az (g)',
      'GX2', 'GY2', 'GZ2', 'Ax2', 'Ay2', 'Az2', 'Ax2 (g)', 'Ay2 (g)', 'Az2 (g)',
      
      // Magnetometer components (all variations)  
      'MX', 'MY', 'MZ', 'Mx', 'My', 'Mz', 'Mx (gauss)', 'My (gauss)', 'Mz (gauss)',
      'Bx', 'By', 'Bz', 'Hx', 'Hy', 'Hz',
      
      // Quality measurements
      'G Error Factor', 'Total G Error Factor',
      
      // Cortex GYRO_SURVEY specific columns
      'Gyro Azimuth (degrees)', 'Gyro Toolface (degrees)', 'Gyro Inclination (degrees)', 
      'Gyro Gravity Toolface (degrees)', 'Gyro Gravity Field (g)', 'Gyro Horizontal Rotation Rate (deg/hr)'
    ];
    
    const allHeaders = Object.keys(rawData[0] || {}).filter(h => h !== timeColumnName);
    const surveyHeaders = allHeaders.filter(header => 
      allowedSurveyColumns.some(allowed => 
        header.toLowerCase().trim() === allowed.toLowerCase().trim()
      )
    );
    const curves: Curve[] = surveyHeaders.map(header => {
      // Extract Y values directly from raw data (no pre-filtering - timeSeriesCleanup will handle this)
      const allYValues = rawData.map(row => parseFloat(row[header]) || 0);
      
      // Extract timestamps directly from the surveys (aligned with Y values)
      const curveTimestamps = surveys.map(s => s.time);
      
      // Get column mapping for clean display name while preserving original for unit extraction
      const columnMapping = ColumnNameMapper.getColumnMapping(header, config);
      
      // Create initial curve
      const initialCurve: Curve = {
        name: columnMapping.displayName,
        xValues: curveTimestamps,
        yValues: allYValues,
        yUnit: columnMapping.unit || getUnitForColumn(columnMapping.originalName)
      };
      
      // Apply data cleaning: sort by timestamp, remove pre-2010 and post-today timestamps
      const cleanupResult = cleanTimeSeriesData(initialCurve);
      
      // Log cleaning results for debugging
      if (cleanupResult.warnings.length > 0) {
        console.log(`🧹 Data cleaning for survey curve "${header}":`, {
          originalPoints: cleanupResult.cleanupSummary.originalCount,
          finalPoints: cleanupResult.cleanupSummary.finalCount,
          timeOutliersRemoved: cleanupResult.cleanupSummary.timeOutliersRemoved,
          invalidYValuesRemoved: cleanupResult.cleanupSummary.invalidYValuesRemoved,
          warnings: cleanupResult.warnings
        });
      }
      
      return cleanupResult.cleanedCurve;
    });

    const surveyData: ParsedData = {
      toolType: config.toolType,
      fileType: config.fileType,
      surveys,
      curves,
      columnInfo: {
        inclination: { unit: 'Degrees', description: 'Wellbore inclination' },
        azimuth: { unit: 'Degrees', description: 'Wellbore azimuth' },
      },
      metadata,
      dataPreview: {
        first5: rawData.slice(0, 5),
        last5: rawData.slice(-5),
        totalCount: rawData.length
      },
      errors: parseErrors
    };
    
    return surveyData;
  }
}